import React from 'react';

import {
  FormControl,
  FormLabel,
  FormLabelProps,
  Select as ChakraSelect,
  FormControlProps as ChakraSelectProps,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  SystemStyleObject,
} from '@chakra-ui/react';
import { TypographyProps } from '@chakra-ui/styled-system';

import { Skeleton } from 'components/uikit/Skeleton';

import { getInputIcon } from './getInputIcon';
import { getInputVariant } from './getInputVariant';
import { getMessageColorForStatus, InputStatus } from './InputStatus';
import { BoxProps, Text } from '../../reexport';

export interface SelectProps<T>
  extends Omit<ChakraSelectProps, 'onFocus' | 'onBlur' | 'onChange'> {
  name?: string;
  id?: string;
  label?: string;
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  placeholder?: string;
  value?: string | number;
  reset?: () => void;
  onChange?: (value: T) => void;
  inputStatus?: InputStatus;
  message?: string;
  onFocus?: React.FocusEventHandler<HTMLSelectElement>;
  onBlur?: React.FocusEventHandler<HTMLSelectElement>;
  isLoading?: boolean;
  fontWeight?: TypographyProps['fontWeight'];
  labelProps?: FormLabelProps;
  hasStatusIcon?: boolean;
  errorMessageWidth?: string;
  _placeholder?: SystemStyleObject;
  autoFocus?: boolean;
  iconWidth?: BoxProps['w'];
  rightIconWidth?: BoxProps['w'];
  ref?: React.RefObject<HTMLSelectElement>;
  isValid?: boolean;
  isNumeric?: boolean;
}

export function Select<T>({
  name,
  id,
  label,
  icon,
  placeholder,
  value,
  onChange,
  message,
  inputStatus,
  onFocus,
  onBlur,
  isLoading = false,
  reset,
  labelProps,
  fontWeight = 'medium',
  maxH,
  hasStatusIcon = true,
  _placeholder,
  errorMessageWidth,
  autoFocus = false,
  iconWidth,
  rightIconWidth,
  ref,
  isValid,
  isNumeric,
  children,
  size,
  ...props
}: SelectProps<T>) {
  const rightIcon = props.rightIcon ?? getInputIcon(inputStatus);

  return (
    <FormControl
      w='100%'
      display='flex'
      flexDir='column'
      rowGap={label ? '6px' : undefined}
      position='relative'
      {...props}
    >
      <FormLabel
        color='grey.secondaryText'
        fontSize='sm'
        fontWeight='medium'
        htmlFor={id}
        lineHeight='20px'
        m='0 0 0 2px'
        {...labelProps}
      >
        {label}
      </FormLabel>
      <Skeleton isLoaded={!isLoading} variant='input'>
        <InputGroup size={size} variant={getInputVariant(inputStatus)}>
          {icon ? (
            <InputLeftElement h={props.height ?? props.h} w={iconWidth}>
              {icon}
            </InputLeftElement>
          ) : null}
          <ChakraSelect
            id={id}
            name={name}
            maxH={maxH}
            placeholder={placeholder}
            disabled={inputStatus === 'DISABLED'}
            value={value}
            onChange={
              onChange ? (e) => onChange(e.target.value as T) : undefined
            }
            backgroundColor='grey.white'
            onFocus={onFocus}
            onBlur={onBlur}
            fontWeight={fontWeight}
            _placeholder={{
              fontWeight: 'regular',
              ..._placeholder,
            }}
            sx={{
              _disabled: {
                opacity: 1,
                backgroundColor: 'grey.border',
                pointerEvents: 'none',
              },
            }}
            _disabled={{ opacity: 1 }}
            autoFocus={autoFocus}
            ref={ref}
            fontFamily={isNumeric ? 'var(--font-numeric)' : undefined}
          >
            {children}
          </ChakraSelect>

          {rightIcon && hasStatusIcon ? (
            <InputRightElement h={props.height ?? props.h} w={rightIconWidth}>
              {rightIcon}
            </InputRightElement>
          ) : null}
        </InputGroup>
      </Skeleton>

      <Text
        fontSize='sm'
        color={getMessageColorForStatus(inputStatus)}
        fontWeight='medium'
        position='absolute'
        width={errorMessageWidth}
        left='2px'
        bottom='-4px'
        transform='translateY(100%)'
      >
        {message}
      </Text>
    </FormControl>
  );
}
